import { useMemo, useRef } from "react";
import classNames from "classnames";
import { useTable, useSortBy, Column, SortingRule } from "react-table";
import { Item } from "@outplayed/tooltips";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { TableSortLabel } from "@ugg/shared/components/common/TableSortLabel";
import { round, numberWithCommas } from "@ugg/shared/utils/math";
import { stackDuplicateItems } from "@ugg/shared/utils/item-helpers";
import { useDragScroll } from "@ugg/shared/hooks/use-drag-scroll";
import { ItemsInfo } from "@ugg/shared/api/data-parser/champions/items";

interface TableBlockProps {
  className?: string;
  title: string;
  data: ItemsInfo[keyof ItemsInfo];
  defaultSort?: SortingRule<ItemsInfo[keyof ItemsInfo]>;
  spriteSize: number;
  patch?: string;
  language?: string;
  ssr?: boolean;
  webp?: boolean;
}

export function RowTableBlock(props: TableBlockProps) {
  const { className, title, data, defaultSort, spriteSize, patch, language, ssr, webp } = props;

  const slider = useRef<HTMLElement>() as React.MutableRefObject<HTMLDivElement | null>;
  useDragScroll(slider);

  const columns = useMemo<Column<(typeof data)[number]>[]>(
    () => [
      {
        accessor: "ids",
        className: "flex-1 mb-[6px] h-[36px]",
        headerClassName: "mb-[6px] h-[36px] text-white",
        Header: () => <div className="text-[14px] font-bold">{title}</div>,
        Cell: ({ cell: { value } }) => {
          const stackedItems = stackDuplicateItems(value);
          return (
            <div className="flex items-center gap-[3px]">
              {stackedItems.map((item) => (
                <div key={item.id} className="relative">
                  <Item
                    className="rounded-[3px]"
                    itemId={item.id}
                    spriteSize={spriteSize}
                    isSprite
                    patch={patch}
                    language={language}
                    ssr={ssr}
                    webp={webp}
                  />
                  {item.count > 1 && (
                    <div
                      className={`
                      absolute bottom-[-4px] right-[-4px] z-10 flex items-center justify-center 
                      w-[14px] h-[14px] text-white text-[11px] font-bold rounded-[50%] border-[1px] 
                      border-purple-400 bg-accent-purple-700 pointer-events-none
                    `}
                    >
                      {item.count}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        accessor: "win_rate",
        className: "py-[4px] h-[24px] text-center",
        headerClassName: "py-[4px] h-[24px] whitespace-nowrap",
        Header: <div>Win Rate</div>,
        Cell: (props) => {
          return <div className="text-[12px]">{`${round(props.value, 2)}% `}</div>;
        },
        sortDescFirst: true,
      },
      {
        accessor: "pick_rate",
        className: "py-[4px] h-[24px] text-center",
        headerClassName: "py-[4px] h-[24px] whitespace-nowrap",
        Header: <div>Pick Rate</div>,
        Cell: ({ cell: { value } }) => {
          return <div className="text-[12px]">{`${round(value, 2)}% `}</div>;
        },
        sortDescFirst: true,
      },
      {
        accessor: "matches",
        className: "py-[4px] h-[24px]  text-center",
        headerClassName: "py-[4px] h-[24px]  whitespace-nowrap",
        Header: <div>Matches</div>,
        Cell: ({ cell: { value } }) => {
          return <div className="text-[12px]">{numberWithCommas(value)}</div>;
        },
        sortDescFirst: true,
      },
    ],
    [data],
  );

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: defaultSort ? [defaultSort] : [{ id: "pick_rate", desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  return (
    <div className={classNames(className, "p-[18px] pb-0 bg-purple-200")}>
      {/* <HeaderBullet className="mb-[18px] whitespace-nowrap">{title}</HeaderBullet> */}
      <div className="flex">
        <div {...getTableProps({ className: "" })}>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps({ className: "flex flex-col w-[90px]" })}>
              {headerGroup.headers.map((column, index) => {
                return (
                  <div className="contents" {...column.getHeaderProps(column.getSortByToggleProps({}))}>
                    <div
                      className={classNames(
                        "flex items-center text-[12px]",
                        {
                          "text-white font-bold": column.isSorted,
                          "text-lavender-50 font-medium": !column.isSorted,
                        },
                        column.headerClassName,
                      )}
                    >
                      {column.render("Header")}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div
          ref={slider}
          {...getTableBodyProps({
            className: "flex flex-1 h-[128px] overflow-x-auto overflow-y-hidden select-none",
            style: {
              /*scrollbarColor: "#5C5C8E #11112A"*/
            },
          })}
        >
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <div
                {...row.getRowProps({
                  className:
                    "flex-[0_0_90px] h-[114px] flex flex-col items-center [&:not(:first-child)]:border-l-[1px] border-accent-purple-700",
                })}
              >
                {row.cells.map((cell, index) => {
                  const tdClassNames = classNames(cell.column.className, "flex items-center justify-center", {
                    "h-[30px] flex-none": index === 0,
                  });
                  return (
                    <div
                      {...cell.getCellProps({
                        className: classNames(tdClassNames, {
                          "text-white font-bold": cell.column.isSorted,
                          "text-lavender-50 font-medium": !cell.column.isSorted,
                        }),
                      })}
                    >
                      {cell.render("Cell", { index: i })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ColumnTableBlock(props: TableBlockProps) {
  const { className, title, data, defaultSort, spriteSize, patch, language, ssr, webp } = props;

  const columns = useMemo<Column<(typeof data)[number]>[]>(
    () => [
      {
        accessor: "ids",
        className: "flex-1",
        Cell: ({ cell: { value } }) => {
          const stackedItems = stackDuplicateItems(value);
          return (
            <div className="flex items-center gap-[6px]">
              {stackedItems.map((item) => (
                <div key={item.id} className="relative">
                  <Item
                    className="rounded-[3px]"
                    itemId={item.id}
                    spriteSize={spriteSize}
                    isSprite
                    patch={patch}
                    language={language}
                    ssr={ssr}
                    webp={webp}
                  />
                  {item.count > 1 && (
                    <div
                      className={`
                      absolute bottom-[-6px] right-[-6px] z-10 flex items-center justify-center 
                      w-[14px] h-[14px] text-white text-[11px] font-bold rounded-[50%] border-[1px] 
                      border-purple-400 bg-accent-purple-700
                    `}
                    >
                      {item.count}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        accessor: "win_rate",
        className: "w-[60px] text-center",
        headerClassName: "w-[60px] whitespace-nowrap",
        Header: <div>Win</div>,
        Cell: (props) => {
          return <div className="text-[12px] font-medium text-lavender-50">{`${round(props.value, 2)}% `}</div>;
        },
        sortDescFirst: true,
      },
      {
        accessor: "pick_rate",
        className: "w-[65px] text-center",
        headerClassName: "w-[65px] whitespace-nowrap",
        Header: <div>Pick</div>,
        Cell: ({ cell: { value } }) => {
          return (
            <div>
              <div className="text-white text-[12px] font-bold">{`${round(value, 2)}% `}</div>
              <div className="text-[11px] font-medium text-lavender-50">{numberWithCommas(value)}</div>
            </div>
          );
        },
        sortDescFirst: true,
      },
    ],
    [data],
  );

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: defaultSort ? [defaultSort] : [{ id: "pick_rate", desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  return (
    <div className={classNames(className)}>
      <div className="flex items-center justify-between p-[18px] bg-purple-200">
        <HeaderBullet className="whitespace-nowrap">{title}</HeaderBullet>
        <div {...getTableProps({ className: "" })}>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps({ className: "flex items-center gap-[24px]" })}>
              {headerGroup.headers.map((column) => {
                return (
                  <div className="contents" {...column.getHeaderProps(column.getSortByToggleProps({}))}>
                    <TableSortLabel
                      className={classNames(
                        "flex items-center justify-center text-[14px]",
                        {
                          "text-white font-semibold": column.isSorted,
                          "text-lavender-50 font-medium": !column.isSorted,
                        },
                        column.headerClassName,
                      )}
                      active={column.isSorted}
                      direction={column.isSortedDesc ? "desc" : "asc"}
                      labelOffset={5}
                    >
                      {column.render("Header")}
                    </TableSortLabel>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div {...getTableBodyProps({ className: "" })}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps({
                className: "flex items-center gap-[24px] px-[18px] py-[12px] border-t-[1px] border-purple-500 bg-purple-200",
              })}
            >
              {row.cells.map((cell) => {
                const tdClassNames = classNames(cell.column.className);
                return <div {...cell.getCellProps({ className: tdClassNames })}>{cell.render("Cell", { index: i })}</div>;
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
